import store from 'Store/index';
import { SET_SELF } from 'Store/reducers/AppReducer';
import logError from 'Helpers/logError';
import UserParamResource from 'App/babel/resources/UserParam';

/**
 * The id of `lastSeenReleaseNoteId` in the master param registry
 * (`base/src/core/params/userParams.js`). Hardcoded because the registry lives in base,
 * which the UI cannot import — the same reason NavbarBrandDropdown names its param by
 * number.
 */
const LAST_SEEN_RELEASE_NOTE_PARAM = 239;

/**
 * Moves the marker in the store, which is what the navbar counts unseen releases from.
 * Never backwards: the marker only ever grows.
 */
export const adoptLastSeenReleaseNoteId = (seenId: number) => {
	const { self } = store.getState().App;
	if (!self || seenId <= (self.userParams.lastSeenReleaseNoteId ?? 0)) {
		return;
	}
	// The raw action rather than `setSelf`: AppActions imports appHelper, which imports this, and
	// `setSelf` would also redo the timezone and locale setup for a read marker.
	store.dispatch({
		type: SET_SELF,
		self: { ...self, userParams: { ...self.userParams, lastSeenReleaseNoteId: seenId } }
	});
};

/** The reader has been shown every release up to `seenId`. */
export const markProductNewsSeen = (seenId: number) => {
	adoptLastSeenReleaseNoteId(seenId);
	// Neither outcome is worth telling the reader about — this is a read marker they did
	// not ask for. A failed write just means the releases count as unseen next load.
	UserParamResource.save(LAST_SEEN_RELEASE_NOTE_PARAM, seenId, {
		skipNotification: true,
		skipErrorNotification: true
	}).catch((e: unknown) => logError(e, 'Product news: could not mark the release notes as seen'));
};

/**
 * Read straight off the param rather than the /alliwant body that carries it: that body
 * is cached in redis for 30 seconds and nothing in the user-param write path invalidates
 * it, so it can still report the value from before this user's own last write.
 */
export const readLastSeenReleaseNoteId = (): Promise<number> =>
	UserParamResource.get(LAST_SEEN_RELEASE_NOTE_PARAM, {
		skipErrorNotification: true,
		// Nothing saved yet answers 404, which is "seen nothing" rather than a failure.
		// Anything else is one, and the caller decides what to do with it.
		expectedStatuses: [404]
	})
		.then((res: { data?: { value?: unknown } | null }) => Number(res?.data?.value) || 0)
		.catch((e: { response?: { status?: number } }) => {
			if (e?.response?.status === 404) {
				return 0;
			}
			throw e;
		});
